import React, { useState, useEffect } from "react";
import "../../css/CRUDModal.css";
import { apiList, baseUrl } from "../../helpers/apiList";
import axios from "axios";
import CustomAlert from "../../helpers/CustomAlert";
import { axiosPostApi } from "../../helpers/interceptorServices";

const AddPickupAddressModal = ({
  show,
  handleClose,
  callApi,
  editData,
  action,
  columnData,
}) => {
  const loggedInUserName = sessionStorage.getItem("username");

  const [formData, setFormData] = useState({
    addressTitle: "",
    addressee: "",
    fullAddress: "",
    phone: "",
    pinCode: "",
    city: "",
    state: "",
    emailId: "",
    optnAddressId: "",
    status: "ACTIVE",
  });

  const [errors, setErrors] = useState({
    addressTitle: "",
    addressee: "",
    fullAddress: "",
    phone: "",
    pinCode: "",
    city: "",
    state: "",
    emailId: "",
  });

  useEffect(() => {
    if (action === "add") {
      setFormData({
        addressTitle: "",
        addressee: "",
        fullAddress: "",
        phone: "",
        pinCode: "",
        city: "",
        state: "",
        emailId: "",
        optnAddressId: "",
        status: "ACTIVE",
      });
    } else if (action === "modify" || action === "view") {
      setFormData({
        ...editData,
        addressTitle: columnData?.addressTitle || "",
        addressee: columnData?.addressee || "",
        fullAddress: columnData?.fullAddress || "",
        phone: columnData?.phone || "",
        pinCode: columnData?.pinCode || "",
        city: columnData?.city || "",
        state: columnData?.state || "",
        emailId: columnData?.emailId || "",
        optnAddressId: columnData?.optnAddressId || "",
        status: columnData?.status || "ACTIVE",
      });
    }
    setErrors({
      addressTitle: "",
      addressee: "",
      fullAddress: "",
      phone: "",
      pinCode: "",
      city: "",
      state: "",
      emailId: "",
    });
  }, [show, action, editData, columnData]);

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    let updatedValue = value;

    // Initialize field-specific errors
    let fieldErrors = { ...errors };

    // Handle field-specific input restrictions and validation
    if (id === "phone") {
      updatedValue = value.replace(/[^0-9]/g, "");
      if (!updatedValue) {
        fieldErrors.phone = "Phone number is required";
      } else if (!/^[789]\d{9}$/.test(updatedValue)) {
        fieldErrors.phone =
          "Phone number must be 10 digits and start with 7, 8, or 9";
      } else if (/^([0-9])\1{9}$/.test(updatedValue)) {
        fieldErrors.phone = "Phone number cannot have all digits the same";
      } else {
        fieldErrors.phone = "";
      }
    } else if (id === "pinCode") {
      updatedValue = value.replace(/[^0-9]/g, "");
      if (!updatedValue) {
        fieldErrors.pinCode = "Pincode is required";
      } else if (!/^[1-9]\d{5}$/.test(updatedValue)) {
        fieldErrors.pinCode = "Pincode must be 6 digits and not start with 0";
      } else {
        fieldErrors.pinCode = "";
      }
    } else if (id === "addressee") {
      if (/^[A-Za-z .-]*$/.test(value)) {
        updatedValue = value;
        if (!updatedValue) {
          fieldErrors.addressee = "Addressee is required";
        } else if (
          !/^[A-Za-z .-]+$/.test(updatedValue) ||
          updatedValue.length < 2 ||
          updatedValue.length > 50
        ) {
          fieldErrors.addressee =
            "Addressee must be 2-50 characters and contain only letters, spaces, dots, or hyphens";
        } else {
          fieldErrors.addressee = "";
        }
      } else {
        return; // Prevent updating if input contains invalid characters
      }
    } else if (id === "addressTitle") {
      updatedValue = value;
      if (!updatedValue) {
        fieldErrors.addressTitle = "Address title is required";
      } else if (
        !/^[A-Za-z0-9@&.,'\- ]+$/.test(updatedValue) ||
        updatedValue.length < 2 ||
        updatedValue.length > 50
      ) {
        fieldErrors.addressTitle =
          "Address title must be valid and between 2 and 50 characters";
      } else {
        fieldErrors.addressTitle = "";
      }
    } else if (id === "fullAddress") {
      updatedValue = value;
      if (!updatedValue) {
        fieldErrors.fullAddress = "Full address is required";
      } else if (updatedValue.length < 5 || updatedValue.length > 150) {
        fieldErrors.fullAddress =
          "Full address must be between 5 and 150 characters";
      } else if (
        !/^[A-Za-z0-9,.\/#-][A-Za-z0-9,.\/# -]*[A-Za-z0-9,.\/#-]$/.test(
          updatedValue
        )
      ) {
        fieldErrors.fullAddress =
          "Full address contains invalid characters or starts/ends with spaces";
      } else {
        fieldErrors.fullAddress = "";
      }
    } else if (id === "emailId") {
      updatedValue = value;
      if (!updatedValue) {
        fieldErrors.emailId = "Email is required";
      } else if (
        !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9-]+\.(com|in|pe|co\.[a-zA-Z]{2})$/.test(
          updatedValue
        ) ||
        /\.com\./.test(updatedValue) ||
        /\.co\.[a-zA-Z]{2}\./.test(updatedValue)
      ) {
        fieldErrors.emailId =
          "Enter valid email ending with .com, .in, .pe, or .co.xx";
      } else {
        fieldErrors.emailId = "";
      }
    }

    // Update form data
    setFormData((prev) => ({
      ...prev,
      [id]: updatedValue,
    }));

    // Update errors
    setErrors(fieldErrors);

    // Trigger pinCode API validation if pinCode changes
    if (id === "pinCode") {
      validatePinCode(updatedValue);
    }
  };

  const validate = () => {
    const newErrors = {};

    // Address Title validation
    if (!formData.addressTitle) {
      newErrors.addressTitle = "Address title is required";
    } else if (
      !/^[A-Za-z0-9@&.,'\- ]+$/.test(formData.addressTitle) ||
      formData.addressTitle.length < 2 ||
      formData.addressTitle.length > 50
    ) {
      newErrors.addressTitle =
        "Address title must be valid and between 2 and 50 characters";
    }

    // Addressee validation
    if (!formData.addressee) {
      newErrors.addressee = "Addressee is required";
    } else if (
      !/^[A-Za-z .-]+$/.test(formData.addressee) ||
      formData.addressee.length < 2 ||
      formData.addressee.length > 50
    ) {
      newErrors.addressee =
        "Addressee must be 2-50 characters and contain only letters, spaces, dots, or hyphens";
    }

    // Full Address validation
    if (!formData.fullAddress) {
      newErrors.fullAddress = "Full address is required";
    } else if (
      formData.fullAddress.length < 5 ||
      formData.fullAddress.length > 150
    ) {
      newErrors.fullAddress =
        "Full address must be between 5 and 150 characters";
    } else if (
      !/^[A-Za-z0-9,.\/#-][A-Za-z0-9,.\/# -]*[A-Za-z0-9,.\/#-]$/.test(
        formData.fullAddress
      )
    ) {
      newErrors.fullAddress =
        "Full address contains invalid characters or starts/ends with spaces";
    }

    // Phone validation
    if (!formData.phone) {
      newErrors.phone = "Phone number is required";
    } else if (!/^[789]\d{9}$/.test(formData.phone)) {
      newErrors.phone =
        "Phone number must be 10 digits and start with 7, 8, or 9";
    } else if (/^([0-9])\1{9}$/.test(formData.phone)) {
      newErrors.phone = "Phone number cannot have all digits the same";
    }

    // Pincode validation
    if (!formData.pinCode) {
      newErrors.pinCode = "Pincode is required";
    } else if (!/^[1-9]\d{5}$/.test(formData.pinCode)) {
      newErrors.pinCode = "Pincode must be 6 digits and not start with 0";
    }

    // City validation
    if (!formData.city) {
      newErrors.city = "City is required";
    }

    // State validation
    if (!formData.state) {
      newErrors.state = "State is required";
    }

    // Email validation
    if (!formData.emailId) {
      newErrors.emailId = "Email is required";
    } else if (
      !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9-]+\.(com|in|pe|co\.[a-zA-Z]{2})$/.test(
        formData.emailId
      ) ||
      /\.com\./.test(formData.emailId) ||
      /\.co\.[a-zA-Z]{2}\./.test(formData.emailId)
    ) {
      newErrors.emailId =
        "Enter valid email ending with .com, .in, .pe, or .co.xx";
    }

    return newErrors;
  };

  const handleBlur = (e) => {
    const { id, value } = e.target;
    const fieldErrors = {};
    const trimmedValue = value?.trim();

    switch (id) {
      case "addressTitle":
        if (!trimmedValue) {
          fieldErrors.addressTitle = "Address title is required";
        } else if (
          !/^[A-Za-z0-9@&.,'\- ]+$/.test(trimmedValue) ||
          trimmedValue.length < 2 ||
          trimmedValue.length > 50
        ) {
          fieldErrors.addressTitle =
            "Address title must be valid and between 2 and 50 characters";
        }
        break;

      case "addressee":
        if (!trimmedValue) {
          fieldErrors.addressee = "Addressee is required";
        } else if (
          !/^[A-Za-z .-]+$/.test(trimmedValue) ||
          trimmedValue.length < 2 ||
          trimmedValue.length > 50
        ) {
          fieldErrors.addressee =
            "Addressee must be 2-50 characters and contain only letters, spaces, dots, or hyphens";
        }
        break;

      case "fullAddress":
        if (!trimmedValue) {
          fieldErrors.fullAddress = "Full address is required";
        } else if (trimmedValue.length < 5 || trimmedValue.length > 150) {
          fieldErrors.fullAddress =
            "Full address must be between 5 and 150 characters";
        } else if (
          !/^[A-Za-z0-9,.\/#-][A-Za-z0-9,.\/# -]*[A-Za-z0-9,.\/#-]$/.test(
            trimmedValue
          )
        ) {
          fieldErrors.fullAddress =
            "Full address contains invalid characters or starts/ends with spaces";
        }
        break;

      case "phone":
        if (!trimmedValue) {
          fieldErrors.phone = "Phone number is required";
        } else if (!/^[789]\d{9}$/.test(trimmedValue)) {
          fieldErrors.phone =
            "Phone number must be 10 digits and start with 7, 8, or 9";
        } else if (/^([0-9])\1{9}$/.test(trimmedValue)) {
          fieldErrors.phone = "Phone number cannot have all digits the same";
        }
        break;

      case "pinCode":
        if (!trimmedValue) {
          fieldErrors.pinCode = "Pincode is required";
        } else if (!/^[1-9]\d{5}$/.test(trimmedValue)) {
          fieldErrors.pinCode =
            "Pincode must be 6 digits and must not start with 0";
        }
        break;

      case "state":
        if (!trimmedValue) {
          fieldErrors.state = "State is required";
        }
        break;

      case "emailId":
        if (!trimmedValue) {
          fieldErrors.emailId = "Email is required";
        } else if (
          !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9-]+\.(com|in|pe|co\.[a-zA-Z]{2})$/.test(
            trimmedValue
          ) ||
          /\.com\./.test(trimmedValue) ||
          /\.co\.[a-zA-Z]{2}\./.test(trimmedValue)
        ) {
          fieldErrors.emailId =
            "Enter valid email ending with .com, .in, .pe, or .co.xx";
        }
        break;

      default:
        break;
    }

    setErrors((prev) => ({
      ...prev,
      ...fieldErrors,
    }));
  };

  const validatePinCode = (pinCode) => {
    let newErrors = { ...errors };

    if (!pinCode) {
      newErrors.pinCode = "Pincode is required";
    } else if (!/^\d{6}$/.test(pinCode)) {
      newErrors.pinCode = "Pincode must be 6 digits";
    } else {
      delete newErrors.pinCode;

      axios
        .get(`${baseUrl}/sms/pincode/${pinCode}`)
        .then((response) => {
          const { status, message, data } = response.data;

          if (!status || !data || data.length === 0) {
            newErrors.pinCode = message || "Enter a valid Pincode";
            setFormData((prevFormData) => ({
              ...prevFormData,
              city: "",
              state: "",
            }));
          } else {
            const pinCodeData = data[0];
            const { cityName, state } = pinCodeData;
            setFormData((prevFormData) => ({
              ...prevFormData,
              city: cityName,
              state,
            }));
            delete newErrors.city;
            delete newErrors.state;
          }
          setErrors(newErrors);
        })
        .catch((error) => {
          console.error("Error fetching pincode data:", error);
          newErrors.pinCode = "Error fetching pincode data";
          setFormData((prevFormData) => ({
            ...prevFormData,
            city: "",
            state: "",
          }));
          setErrors(newErrors);
        });
    }
  };

  const isViewMode = action === "view";
  const isReadOnly = isViewMode;

  const handleBack = () => {
    handleClose();
    setErrors({});
  };

  const handleAddPickupAddressModal = () => {
    const validationErrors = validate();
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      let apiUrl = "";
      if (action === "add") {
        apiUrl = apiList.CREATE_PICKUPADDRESS;
      } else if (action === "modify") {
        apiUrl = apiList.MODIFY_PICKUPADDRESS;
      } else {
        return; // Only allow add or modify actions
      }

      const payload = {
        username: loggedInUserName,
        ...(action === "modify" && {
          addressId: columnData?.addressId || columnData?.id,
        }),
        addressTitle: formData.addressTitle || undefined,
        addressee: formData.addressee || undefined,
        fullAddress: formData.fullAddress || undefined,
        phone: formData.phone || undefined,
        pinCode: formData.pinCode || undefined,
        city: formData.city || undefined,
        state: formData.state || undefined,
        emailId: formData.emailId || undefined,
        optnAddressId: formData.optnAddressId || undefined,
        status: formData.status || "ACTIVE",
      };

      axiosPostApi(apiUrl, payload)
        .then((response) => {
          if (response?.status === 200 && response?.data?.status === true) {
            CustomAlert({
              title: response?.data?.message,
              icon: "success",
              confirmButtonText: "OK",
              showCloseButton: true,
              allowOutsideClick: false,
            });
            callApi(1);
            handleClose();
          } else if (response?.status === 200 && !response?.data?.status) {
            CustomAlert({
              title: "Failed to Request",
              text: response.data.message,
              icon: "error",
              confirmButtonText: "Retry",
            });
          }

          if (response?.data?.respCode === "02") {
            CustomAlert({
              title: "Warning",
              text:
                response?.data?.message || "Unable to proceed, Request again",
              icon: "warning",
              confirmButtonText: "Okay",
            });
          }
        })
        .catch((error) => {
          console.error("Error processing request:", error);
          CustomAlert({
            title: "Error",
            text: "An error occurred while adding the request. Please try again later.",
            icon: "error",
            confirmButtonText: "Retry",
          });
        });
    }
  };

  return (
    <>
      {show && (
        <div
          style={{
            position: "fixed",
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            backgroundColor: "rgba(0,0,0,0.4)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1031,
          }}
        >
          <div className="modal-dialog" role="document">
            <div
              className="modal-content"
              style={{
                width: "597px",
                borderRadius: "24px",
                padding: "24px",
                backgroundColor: "#FFF",
                border: "1px solid rgba(0, 0, 0, 0.175)",
              }}
            >
              <div
                className="modal-header"
                style={{
                  padding: "1rem 1rem",
                  borderBottom: "1px solid #dee2e6",
                }}
              >
                <h5 className="modal-title main-title">
                  {action === "add"
                    ? "Add"
                    : action === "modify"
                    ? "Modify"
                    : "View"}{" "}
                  Pickup Address Details
                </h5>
              </div>
              <form>
                <div
                  className="scoped-modal modal-body"
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "24px",
                  }}
                >
                  <div className="row mt-2">
                    <div
                      className="scoped-modal modal-body"
                      style={{ paddingBlock: "0px" }}
                    >
                      <div className="row mb-3">
                        <div className="col-md-6">
                          <div className="form-group">
                            <label htmlFor="addressTitle">
                              Address Title
                              <span style={{ color: "red" }}>*</span>
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Enter Address Title"
                              id="addressTitle"
                              value={formData.addressTitle}
                              onChange={handleInputChange}
                              onBlur={handleBlur}
                              disabled={isReadOnly}
                            />
                            {!isReadOnly && errors.addressTitle ? (
                              <span className="error">
                                {errors.addressTitle}
                              </span>
                            ) : null}
                          </div>
                        </div>

                        <div className="col-md-6">
                          <div className="form-group">
                            <label htmlFor="addressee">
                              Addressee
                              <span style={{ color: "red" }}>*</span>
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Enter Addressee"
                              id="addressee"
                              value={formData.addressee}
                              disabled={isReadOnly}
                              maxLength={50}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (/^[A-Za-z .-]*$/.test(value)) {
                                  handleInputChange(e);
                                }
                              }}
                              onBlur={handleBlur}
                            />
                            {!isReadOnly && errors.addressee ? (
                              <span className="error">{errors.addressee}</span>
                            ) : null}
                          </div>
                        </div>
                      </div>
                      <div className="row mb-3">
                        <div className="col-md-12">
                          <div className="form-group">
                            <label htmlFor="fullAddress">
                              Full Address
                              <span style={{ color: "red" }}>*</span>
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Enter Full Address"
                              id="fullAddress"
                              value={formData.fullAddress}
                              disabled={isReadOnly}
                              onChange={handleInputChange}
                              onBlur={handleBlur}
                            />
                            {!isReadOnly && errors.fullAddress ? (
                              <span className="error">
                                {errors.fullAddress}
                              </span>
                            ) : null}
                          </div>
                        </div>
                      </div>
                      <div className="row mb-3">
                        <div className="col-md-6">
                          <div className="form-group">
                            <label htmlFor="pinCode">
                              PIN Code
                              <span style={{ color: "red" }}>*</span>
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Enter PIN Code"
                              id="pinCode"
                              value={formData.pinCode}
                              disabled={isReadOnly}
                              maxLength={6}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (/^\d{0,6}$/.test(value)) {
                                  handleInputChange(e);
                                }
                              }}
                              onBlur={handleBlur}
                            />
                            {!isReadOnly && errors.pinCode ? (
                              <span className="error">{errors.pinCode}</span>
                            ) : null}
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-group">
                            <label htmlFor="city">City</label>
                            <input
                              type="text"
                              className="form-control"
                              id="city"
                              value={formData.city || "City"}
                              style={{ cursor: "not-allowed" }}
                              readOnly
                            />
                            {!isReadOnly && errors.city ? (
                              <span className="error">{errors.city}</span>
                            ) : null}
                          </div>
                        </div>
                      </div>
                      <div className="row mb-3">
                        <div className="col-md-6">
                          <div className="form-group">
                            <label htmlFor="state">State</label>
                            <input
                              type="text"
                              className="form-control"
                              id="state"
                              value={formData.state || "State"}
                              style={{ cursor: "not-allowed" }}
                              readOnly
                            />
                            {!isReadOnly && errors.state ? (
                              <span className="error">{errors.state}</span>
                            ) : null}
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-group">
                            <label htmlFor="phone">
                              Phone
                              <span style={{ color: "red" }}>*</span>
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Enter Phone"
                              id="phone"
                              value={formData.phone}
                              disabled={isReadOnly}
                              maxLength={10}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (/^\d{0,10}$/.test(value)) {
                                  handleInputChange(e);
                                }
                              }}
                              onBlur={handleBlur}
                            />
                            {!isReadOnly && errors.phone ? (
                              <span className="error">{errors.phone}</span>
                            ) : null}
                          </div>
                        </div>
                      </div>
                      <div className="row mb-3">
                        <div className="col-md-12">
                          <div className="form-group">
                            <label htmlFor="emailId">
                              Email
                              <span style={{ color: "red" }}>*</span>
                            </label>
                            <input
                              type="email"
                              className="form-control"
                              placeholder="Enter Email"
                              id="emailId"
                              value={formData.emailId}
                              disabled={isReadOnly}
                              onChange={handleInputChange}
                              onBlur={handleBlur}
                            />
                            {!isReadOnly && errors.emailId ? (
                              <span className="error">{errors.emailId}</span>
                            ) : null}
                          </div>
                        </div>
                      </div>
                      <hr />
                    </div>
                  </div>
                </div>

                <div
                  className="scoped-modal"
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    padding: "12px",
                    paddingTop: "12px",
                    gap: "16px",
                  }}
                >
                  <button
                    type="button"
                    className="btn btn-back"
                    onClick={handleBack}
                  >
                    Cancel
                  </button>
                  {!isReadOnly && (action === "add" || action === "modify") && (
                    <button
                      type="button"
                      className="btn btn-submit"
                      onClick={handleAddPickupAddressModal}
                    >
                      {action === "modify" ? "Update" : "Submit"}
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AddPickupAddressModal;
